import {GridBreakPointKeys, GridBreakpoints} from "app/Interface/Content/Grid/WidgetGrid"
import Logger from "logger/logger"
import {useEffect, useState} from "react"
import {Subscription} from "rxjs"
import {systemLayoutQuery, systemLayoutStore} from "store/system/layout/system.layout.store"
import {onEmit} from "utils/onEmit"

export const SubscribeSystemLayoutUiService = {
  activeTabId: 'activeTabId',
  breakpoints: 'breakpoints',
  breakpoint: 'breakpoint',
  forceBreakpoint: 'forceBreakpoint',
  editLayout: 'editLayout',
  isDeleting: 'isDeleting',
  layouts: 'layouts'
}

function SystemLayoutUiService(subscribe: string[], caller: string) {

  const subscribed: Subscription[] = []

  const [systemLayoutsUi, setSystemLayoutsUi] = useState<any[]>([])
  const [activeTabId, setActiveTabId] = useState<string>()
  const [breakpoints, setBreakpoints] = useState<GridBreakpoints>()
  const [breakpoint, setBreakpointLocal] = useState<string>()
  const [forceBreakpoint, setForceBreakpointLocal] = useState<GridBreakPointKeys>()
  const [editLayoutId, setEditLayoutIdLocal] = useState<string>()
  const [isEditingLayout, setIsEditingLayout] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    if (!subscribe) throw Error(`Service.System.Layout.UI: Define what you want to subscribe to`)
    if (!caller) throw Error("Service.System.Layout.UI: Define where you are calling from")

    Logger(`Service.System.Layout.UI.init.${caller}`, subscribe.join(', '))

    if (subscribe.includes(SubscribeSystemLayoutUiService.activeTabId))
      subActiveTab()

    if (subscribe.includes(SubscribeSystemLayoutUiService.breakpoints))
      subBreakpoints()

    if (subscribe.includes(SubscribeSystemLayoutUiService.breakpoint))
      subBreakpoint()

    if (subscribe.includes(SubscribeSystemLayoutUiService.forceBreakpoint))
      subForceBreakpoint()

    if (subscribe.includes(SubscribeSystemLayoutUiService.editLayout))
      subEditLayout()

    if (subscribe.includes(SubscribeSystemLayoutUiService.isDeleting))
      subIsDeleting()

    if (subscribe.includes(SubscribeSystemLayoutUiService.layouts))
      subAllLayouts()

    return () => {
      Logger(`Service.System.Layout.UI.unloading.${caller}`)
      subscribed.map(s => s.unsubscribe())
    }
  }, [])

  const setTabActive = (tabId: string | undefined) => {
    Logger('Service.System.Layout.UI', caller, 'setTabActive', 'tabId', tabId)
    systemLayoutStore.ui.updateActive(state => {
      state.activeTab = tabId
    })
  }

  const setBreakpoint = (bp: GridBreakPointKeys | undefined) => {
    systemLayoutStore.ui.update(state => {
      state.breakpoint = bp
    })
  }

  const setEditLayout = (layoutId: string | undefined) => {
    systemLayoutStore.ui.update(state => {
      state.editLayout = layoutId
    })
  }

  const setForceBreakpoint = (breakpoint: GridBreakPointKeys) => {
    systemLayoutStore.ui.update(state => {
      state.forceBreakpoint = breakpoint
    })
  }

  return {
    systemLayoutsUi,
    breakpoints,
    breakpoint,
    forceBreakpoint,
    activeTabId,
    editLayoutId,
    isEditingLayout,
    isDeleting,
    setTabActive,
    setBreakpoint,
    setEditLayout,
    setForceBreakpoint,
  }

  function subAllLayouts() {
    subscribed.push(onEmit(systemLayoutQuery.ui.selectAll(), newAll => {
      setSystemLayoutsUi(newAll)
    }))
  }

  function subActiveTab() {
    subscribed.push(onEmit(systemLayoutQuery.ui.selectActive(), activeLayout => {
      Logger(`Service.System.Layout.UI.push.activeLayout.${caller}`, activeLayout)
      setActiveTabId(activeLayout?.activeTab)
    }))
  }

  function subBreakpoints() {
    subscribed.push(onEmit(systemLayoutQuery.ui.select('breakpoints'), (newBreakpoints) => {
      Logger(`Service.System.Layout.UI.push.breakpoints.${caller}`, newBreakpoints)
      if (undefined === newBreakpoints) return
      // @ts-ignore
      const newStuff: GridBreakpoints = {}
      const keys = Object.keys(newBreakpoints) as GridBreakPointKeys[]
      keys.sort((a, b) => newBreakpoints[b] - newBreakpoints[a])
      .map(a => {
        newStuff[a] = newBreakpoints[a]
      })
      setBreakpoints(newStuff)
    }))
  }

  function subBreakpoint() {
    subscribed.push(onEmit(systemLayoutQuery.ui.select('breakpoint'), newBreakpoint => {
      Logger(`Service.System.Layout.UI.push.breakpoint.${caller}`, newBreakpoint)
      setBreakpointLocal(newBreakpoint)
    }))
  }

  function subForceBreakpoint() {
    subscribed.push(onEmit(systemLayoutQuery.ui.select('forceBreakpoint'), newForceBreakpoint => {
      Logger(`Service.System.Layout.UI.push.forceBreakpoint.${caller}`, forceBreakpoint)
      setForceBreakpointLocal(newForceBreakpoint)
    }))
  }

  function subEditLayout() {
    subscribed.push(onEmit(systemLayoutQuery.ui.select('editLayout'), newEditLayout => {
      Logger(`Service.System.Layout.UI.push.editLayout.${caller}`, newEditLayout, !!newEditLayout)
      setEditLayoutIdLocal(newEditLayout)
      setIsEditingLayout(!!newEditLayout)
    }))
  }

  function subIsDeleting() {
    subscribed.push(onEmit(systemLayoutQuery.ui.select('isDeleting'), newIsDeleting => {
      Logger(`Service.System.Layout.UI.push.isDeleting.${caller}`, newIsDeleting)
      setIsDeleting(newIsDeleting)
    }))
  }
}

export default SystemLayoutUiService
